// Animation and visual updates for pull-to-load indicator

import { getTranslation } from '../helpers.js';
import { isFulltextTabActive } from './utils.js';
import { 
    getPullToLoadState,
    getPullIndicator,
    setPullIndicator,
    getAnimationFrameId,
    setAnimationFrameId,
    getTargetDistance,
    getCurrentDisplayDistance,
    setCurrentDisplayDistance,
    getImmediateTranslateYDistance,
    setImmediateTranslateYDistance
} from './state.js';
import { MIN_VISIBLE_DISTANCE, LOAD_THRESHOLD } from './constants.js';

/**
 * Start smooth animation loop for indicator updates
 * All properties use smooth interpolation for gentle, soft feel
 */
export function startSmoothAnimation() {
    if (getAnimationFrameId()) {
        return; // Already animating
    }
    
    const animate = () => {
        const targetDistance = getTargetDistance();
        let currentDisplayDistance = getCurrentDisplayDistance();
        
        // Smooth interpolation towards target distance with gentle easing
        const diff = targetDistance - currentDisplayDistance;
        if (Math.abs(diff) < 0.1) {
            currentDisplayDistance = targetDistance;
            setCurrentDisplayDistance(targetDistance);
            setImmediateTranslateYDistance(targetDistance);
            setAnimationFrameId(null);
        } else {
            // Gentle, soft interpolation (lower factor = smoother, less reactive)
            // 0.04 = very smooth and gentle, 2x slower than 0.08
            currentDisplayDistance += diff * 0.06;
            setCurrentDisplayDistance(currentDisplayDistance);
            setImmediateTranslateYDistance(currentDisplayDistance);
            const frameId = requestAnimationFrame(animate);
            setAnimationFrameId(frameId);
        }
        
        // Update indicator with smooth distance (all properties interpolated smoothly)
        // Pass wasLoading flag to prevent text change after loading
        const state = getPullToLoadState();
        updatePullIndicator(currentDisplayDistance, false, true, state.justFinishedLoading);
    };
    
    const frameId = requestAnimationFrame(animate);
    setAnimationFrameId(frameId);
}

/**
 * Update pull indicator with wave animation
 * @param {number} distance - Current pull distance
 * @param {boolean} reset - Whether to reset animation
 * @param {boolean} immediateTranslateY - If true, use distance directly for translateY (no interpolation)
 * @param {boolean} wasLoading - Flag to prevent text flickering after loading
 */
export function updatePullIndicator(distance, reset = false, immediateTranslateY = false, wasLoading = false) {
    // Only update if fulltext tab is active
    if (!isFulltextTabActive()) {
        return;
    }
    
    const resultsContainer = document.querySelector('#fulltext-results-container');
    if (!resultsContainer) return;
    
    const state = getPullToLoadState();
    let pullIndicator = getPullIndicator();
    
    // Check if we're in loading state (before we remove it)
    const isInLoadingState = pullIndicator && pullIndicator.classList.contains('pull-to-load-loading');
    // Skip text update if: explicitly passed wasLoading, or in loading state and resetting, or just finished loading
    const shouldSkipTextUpdate = wasLoading || (isInLoadingState && reset) || state.justFinishedLoading;
    
    // Create indicator with Ultra spinner integrated with arrow if it doesn't exist
    if (!pullIndicator) {
        pullIndicator = document.createElement('div');
        pullIndicator.className = 'pull-to-load-indicator';
        pullIndicator.innerHTML = `
            <div class="pull-to-load-content">
                <div class="pull-to-load-icon-wrapper">
                    <div class="pull-to-load-spinner-ultra spinner-ultra-mini"></div>
                    <div class="pull-to-load-icon">
                        <i class="bi bi-arrow-down"></i>
                    </div>
                </div>
                <div class="pull-to-load-text">${getTranslation('pull_to_load', 'Pull to load more')}</div>
            </div>
        `;
        resultsContainer.appendChild(pullIndicator);
        setPullIndicator(pullIndicator);
    }
    
    // Calculate progress (0-1) - gradual from MIN_VISIBLE_DISTANCE to LOAD_THRESHOLD
    const effectiveDistance = Math.max(0, distance - MIN_VISIBLE_DISTANCE);
    const progressRange = LOAD_THRESHOLD - MIN_VISIBLE_DISTANCE;
    const progress = Math.min(1, effectiveDistance / progressRange);
    
    // Show indicator gradually even at small distances (for visual feedback)
    const shouldShow = distance >= MIN_VISIBLE_DISTANCE;
    const shouldLoad = distance >= LOAD_THRESHOLD;
    
    // Calculate vertical position (waves rise from bottom)
    // At distance 0: translateY(100%) - fully hidden below
    // At LOAD_THRESHOLD: translateY(0%) - fully visible
    // Use immediate distance for translateY if immediateTranslateY is true (for responsive scrolling)
    const distanceForTranslateY = immediateTranslateY ? getImmediateTranslateYDistance() : distance;
    const effectiveDistanceForTranslateY = Math.max(0, distanceForTranslateY - MIN_VISIBLE_DISTANCE);
    const progressForTranslateY = Math.min(1, effectiveDistanceForTranslateY / progressRange);
    const maxTranslate = 100; // Maximum translate percentage
    const translateY = Math.max(0, maxTranslate - (progressForTranslateY * maxTranslate));
    
    // Update icon rotation and Ultra spinner based on progress
    const iconElement = pullIndicator.querySelector('.pull-to-load-icon');
    const spinnerUltra = pullIndicator.querySelector('.pull-to-load-spinner-ultra');
    
    if (iconElement && spinnerUltra) {
        // Rotate arrow: 0° (down) at 0% progress → 180° (up) at 100% progress
        const rotation = progress * 180;
        iconElement.style.transform = `rotate(${rotation}deg)`;
        
        // Update Ultra spinner opacity and speed based on progress
        if (progress > 0) {
            spinnerUltra.style.opacity = Math.min(1, progress * 1.5);
            // Speed up spinner as progress increases (faster rotation when ready)
            // Slowed down 10x: base 12s, min 6s (was 1.2s and 0.6s)
            const baseDuration = 4; // Base animation duration in seconds (10x slower)
            const minDuration = 2; // Fastest duration when ready (10x slower)
            const spinnerDuration = baseDuration - (progress * (baseDuration - minDuration));
            spinnerUltra.style.animationDuration = `${spinnerDuration}s`;
        } else {
            spinnerUltra.style.opacity = '0';
        }
        
        // When ready to load, make spinner more prominent
        if (shouldLoad) {
            spinnerUltra.classList.add('pull-to-load-spinner-ready');
        } else {
            spinnerUltra.classList.remove('pull-to-load-spinner-ready');
        }
    }
    
    // Update visibility and animation with gradual appearance
    if (shouldShow) {
        // Gradual opacity based on progress
        const opacity = Math.min(1, progress * 1.5); // Faster opacity increase
        
        // Restore text display if it was hidden (for next use after loading)
        const textElement = pullIndicator.querySelector('.pull-to-load-text');
        if (textElement && textElement.style.display === 'none') {
            textElement.style.display = '';
        }
        
        pullIndicator.style.opacity = opacity;
        pullIndicator.style.transform = `translateY(${translateY}%)`;
        pullIndicator.classList.add('pull-to-load-active');
        pullIndicator.style.setProperty('--progress', progress);
        
        if (shouldLoad) {
            // Only update to "Loading" if not resetting after loading
            if (!shouldSkipTextUpdate) {
                pullIndicator.classList.add('pull-to-load-ready');
                const textElement = pullIndicator.querySelector('.pull-to-load-text');
                // CRITICAL: Don't change text if it's hidden (display: none) - prevents flickering
                if (textElement && textElement.style.display !== 'none') {
                    textElement.textContent = getTranslation('loading', 'Loading...');
                }
            }
            // Hide icon when ready to load
            if (iconElement) {
                iconElement.style.opacity = '0';
            }
        } else {
            // Only update text if not in loading state and not resetting after loading
            // This prevents flickering "Pull to load more" text after page load
            if (!shouldSkipTextUpdate) {
                pullIndicator.classList.remove('pull-to-load-ready');
                const textElement = pullIndicator.querySelector('.pull-to-load-text');
                // CRITICAL: Don't change text if it's hidden (display: none) - prevents flickering
                if (textElement && textElement.style.display !== 'none') {
                    textElement.textContent = getTranslation('pull_to_load', 'Pull to load more');
                }
                // Show icon with rotation based on progress
                if (iconElement) {
                    iconElement.style.opacity = opacity;
                }
            }
        }
    } else {
        // When hiding, immediately remove all states and hide without text change
        // CRITICAL: Don't change text when hiding - this prevents flickering
        pullIndicator.style.opacity = '0';
        pullIndicator.style.transform = `translateY(100%)`;
        pullIndicator.classList.remove('pull-to-load-active', 'pull-to-load-ready', 'pull-to-load-loading');
        pullIndicator.style.setProperty('--progress', '0');
        // DO NOT change text here - leave it as is to prevent flickering
    }
    
    if (reset) {
        setTargetDistance(0);
        setCurrentDisplayDistance(0);
        const animationFrameId = getAnimationFrameId();
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            setAnimationFrameId(null);
        }
        // Immediately hide indicator without showing "Pull to load more" text
        // Remove loading state if present
        // CRITICAL: Don't change text when resetting after loading
        if (pullIndicator) {
            pullIndicator.style.opacity = '0';
            pullIndicator.style.transform = `translateY(100%)`;
            pullIndicator.classList.remove('pull-to-load-active', 'pull-to-load-ready', 'pull-to-load-loading');
            pullIndicator.style.setProperty('--progress', '0');
            // DO NOT change text here - leave it as is to prevent flickering
        }
    }
}

